// PostgreSQL Data Accessor - replaces Firebase
let database;
let databaseInitAttempted = false;
const dataDebugEnabled = Boolean(
  window.__DATA_DEBUG__ || localStorage.getItem('enableDataDebug') === 'true'
);
const dataLog = (...args) => {
  if (dataDebugEnabled) {
    console.log('[data]', ...args);
  }
};

const LOCAL_INFERENCE_HOSTS = new Set([
  'localhost',
  '0.0.0.0',
  '127.0.0.1',
  '::1',
  'helix.local'
]);

function getInferenceApiBaseUrl() {
  const pageOrigin = window.location.origin;
  const pageHostname = String(window.location.hostname || '').toLowerCase();
  const isLocalPage = LOCAL_INFERENCE_HOSTS.has(pageHostname);
  const configured = window.fixtures && window.fixtures.inf_server_url;

  if (typeof configured === 'string' && configured.trim()) {
    try {
      const target = new URL(configured.trim(), pageOrigin);
      const isMixedContent = window.location.protocol === 'https:' && target.protocol === 'http:';
      const isPublicToLocal =
        !isLocalPage && LOCAL_INFERENCE_HOSTS.has(target.hostname.toLowerCase());
      if (!isMixedContent && !isPublicToLocal) {
        return target.origin;
      }
      console.warn('Ignoring browser-unreachable inference server URL; using page origin');
    } catch (error) {
      console.warn('Ignoring invalid inference server URL; using page origin');
    }
  }

  if (isLocalPage) {
    return window.location.protocol + '//' + window.location.hostname + ':6060';
  }
  return pageOrigin;
}

// Helper function to get user secret for API authentication
function getUserSecret() {
  if (window.userData && window.userData.secret) {
    return window.userData.secret;
  }
  const secret = localStorage.getItem('secret');
  if (secret) {
    return secret;
  }
  console.warn('getUserSecret: No secret found in userData or localStorage');
  return null;
}

// Helper function to ensure userData is available before accessing it
function ensureUserData() {
  if (!window.userData || !window.userData.secret) {
    const email = localStorage.getItem('userEmail');
    const secret = localStorage.getItem('secret');

    if (email && secret) {
      window.userData = {
        email: email,
        secret: secret,
        name: localStorage.getItem('userName') || email.split('@')[0],
        id: secret,
        uid: secret,
        is_subscribed: localStorage.getItem('isSubscribed') === 'true'
      };
      dataLog('ensureUserData: Restored userData from localStorage');
      return true;
    }
    console.warn('ensureUserData: No valid auth data available');
    return false;
  }
  return true;
}

// Helper function to make authenticated API calls
async function apiCall(endpoint, method = 'POST', data = {}) {
  const secret = getUserSecret();
  if (!secret) {
    throw new Error('User not authenticated - no secret available');
  }

  // Determine which server to route to based on endpoint
  let baseUrl = window.location.origin;

  // Route heavy data APIs to the inference server. Keep user-settings on main app to avoid CORS/404s.
  if (
    endpoint.includes('/api/dashboards') ||
    endpoint.includes('/api/chats') ||
    endpoint.includes('/api/graphs') ||
    endpoint.includes('/api/upload-file')
  ) {
    // Public pages must never follow stale/misconfigured fixtures to a browser-local
    // service or an HTTP target that the browser will block as mixed content.
    baseUrl = getInferenceApiBaseUrl();
    dataLog('Using inference API URL', baseUrl);
  }

  const config = {
    method,
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include' // Required for CORS requests with credentials
  };

  if (method !== 'GET') {
    config.body = JSON.stringify({ ...data, secret });
  } else if (secret) {
    // For GET requests, add secret as query parameter
    const url = new URL(endpoint, baseUrl);
    url.searchParams.append('secret', secret);
    endpoint = url.toString();
  }

  // Construct full URL if we're not already using a full URL
  const fullUrl = endpoint.startsWith('http') ? endpoint : baseUrl + endpoint;

  dataLog('Making API call to', fullUrl);

  try {
    const response = await fetch(fullUrl, config);

    if (!response.ok) {
      // Graceful fallbacks for known optional endpoints during tests/dev
      if (endpoint.includes('/api/user-settings/get')) {
        console.warn(`user-settings/get ${response.status} - returning defaults`);
        return {
          currentActiveChatName: 'general',
          currentActiveDashboard: 'Untitled'
        };
      }
      if (endpoint.includes('/api/dashboards/list')) {
        console.warn(`dashboards/list ${response.status} - returning empty list`);
        return {};
      }
      console.warn('API call failed:', response.status, response.statusText, 'URL:', fullUrl);
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    // Network errors: provide soft fallbacks to keep UI responsive in tests/dev
    if (endpoint.includes('/api/user-settings/get')) {
      console.warn('user-settings/get network error - returning defaults');
      return {
        currentActiveChatName: 'general',
        currentActiveDashboard: 'Untitled'
      };
    }
    if (endpoint.includes('/api/dashboards/list')) {
      console.warn('dashboards/list network error - returning empty list');
      return {};
    }
    console.warn('Fetch error for URL:', fullUrl, 'Error:', error);
    throw error;
  }
}

// Helper function to get a database reference
function getDatabaseRef() {
  if (database) {
    return database;
  }

  // Initialize PostgreSQL database accessor
  database = getPostgreSQLDatabase();
  dataLog('PostgreSQL database initialized successfully');
  databaseInitAttempted = true;
  return database;
}

// PostgreSQL database implementation using API endpoints
function getPostgreSQLDatabase() {
  // Create a ref object that mimics Firebase's API but uses PostgreSQL endpoints
  function createPostgreSQLRef(path) {
    const pathParts = path.split('/').filter(p => p);
    const snapshot = value => ({
      val: () => value,
      exists: () =>
        value !== null &&
        value !== undefined &&
        (typeof value !== 'object' || Object.keys(value).length > 0)
    });

    return {
      // Set data using PostgreSQL API
      set: async function (data) {
        try {
          const [type, id, scopedName, collection, childId] = pathParts;

          if (type === 'chats') {
            if (collection === 'messages' && childId) {
              return await apiCall('/api/chats/messages/add', 'POST', {
                chat_name: scopedName || data.chat_name || data.name || data.chatName || 'general',
                message_id: childId,
                message_name:
                  data.messageName || data.message_name || data.graphName || data.content || '',
                edits: Array.isArray(data.edits) ? data.edits : []
              });
            }
            if (pathParts.length === 1 || scopedName) {
              return await apiCall('/api/chats/create', 'POST', {
                chat_name: scopedName || data.chat_name || data.name || 'New Chat'
              });
            }
          } else if (type === 'dashboards') {
            if (collection === 'graphs' && childId) {
              return await apiCall('/api/graphs/add', 'POST', {
                dashboard_name:
                  scopedName || data.dashboard_name || data.dashboardName || data.name || 'Untitled',
                graph_id: childId,
                graph_name: data.graphName || data.graph_name || data.title || '',
                edits: Array.isArray(data.edits) ? data.edits : [],
                graph_data: data.graph_data || data.graphData || null,
                query_text: data.queryText || data.query_text || data.graphName || data.graph_name || ''
              });
            }
            if (pathParts.length === 1 || scopedName) {
              return await apiCall('/api/dashboards/create', 'POST', {
                dashboard_name: scopedName || data.dashboard_name || data.name || 'New Dashboard'
              });
            }
          } else if (type === 'graphs') {
            // Adding a graph
            return await apiCall('/api/graphs/add', 'POST', {
              chat_id: data.chat_id,
              dashboard_id: data.dashboard_id,
              title: data.title || '',
              graph_data: data.graph_data || {},
              description: data.description || ''
            });
          } else if (type === 'users') {
            if (pathParts.length === 2) {
              // Update user settings
              return await apiCall('/api/user-settings/update', 'POST', {
                settings: data
              });
            }
          }

          return Promise.resolve();
        } catch (error) {
          console.error('PostgreSQL set error:', error);
          return Promise.reject(error);
        }
      },

      // Get data using PostgreSQL API
      get: async function () {
        try {
          const [type, id, scopedName, collection, childId] = pathParts;

          if (type === 'chats') {
            if (collection === 'messages' && childId) {
              const result = await apiCall('/api/chats/messages/get', 'POST', {
                chat_name: scopedName,
                message_id: childId
              });
              return snapshot(result || { messageName: '', edits: [] });
            }
            if (pathParts.length <= 2) {
              const result = await apiCall('/api/chats/list', 'POST');
              const chats = result.chats || result || {};
              return {
                val: () => chats,
                exists: () => chats && Object.keys(chats).length > 0
              };
            }
            if (scopedName) {
              const result = await apiCall('/api/chats/list', 'POST');
              const chats = result.chats || result || {};
              return snapshot(chats[scopedName] || null);
            }
          } else if (type === 'dashboards') {
            if (collection === 'graphs' && childId) {
              const result = await apiCall('/api/graphs/get', 'POST', { graph_id: childId });
              return snapshot(result || { graphName: '', edits: [] });
            }
            if (pathParts.length <= 2) {
              const result = await apiCall('/api/dashboards/list', 'POST');
              return {
                val: () => result || {},
                exists: () => result && Object.keys(result).length > 0
              };
            }
            if (scopedName) {
              const result = await apiCall('/api/dashboards/list', 'POST');
              return snapshot(result && result[scopedName] ? result[scopedName] : null);
            }
          } else if (type === 'users') {
            if (pathParts.length === 2) {
              // Get user settings
              try {
                const result = await apiCall('/api/user-settings/get', 'POST');
                return {
                  // Backend returns flat fields: { currentActiveChatName, currentActiveDashboard }
              val: () => result || {},
              exists: () => !!result
            };
          } catch (error) {
            // If user settings don't exist yet, return empty but valid response
            dataLog('User settings not found, returning defaults');
            return {
              val: () => ({ currentActiveChatName: 'Default' }),
              exists: () => true
            };
          }
            }
          }

          return {
            val: () => null,
            exists: () => false
          };
        } catch (error) {
          // Don't log as error if user is simply not authenticated - that's expected on public pages
          if (error.message && error.message.includes('not authenticated')) {
            dataLog('PostgreSQL get skipped - user not authenticated');
          } else {
            console.error('PostgreSQL get error:', error);
          }
          return {
            val: () => null,
            exists: () => false
          };
        }
      },

      // Update data using PostgreSQL API
      update: async function (updates) {
        try {
          // For updates, we'll use the set method with merged data
          return await this.set(updates);
        } catch (error) {
          console.error('PostgreSQL update error:', error);
          return Promise.reject(error);
        }
      },

      // Remove data using PostgreSQL API
      remove: async function () {
        try {
          const [type, _secret, scopedName, collection, childId] = pathParts;

          if (type === 'chats') {
            if (collection === 'messages' && childId) {
              return await apiCall('/api/chats/messages/delete', 'POST', {
                chat_name: scopedName,
                message_id: childId
              });
            }
            if (scopedName) {
              return await apiCall('/api/chats/delete', 'POST', {
                chat_name: scopedName
              });
            }
          } else if (type === 'dashboards') {
            if (collection === 'graphs' && childId) {
              return await apiCall('/api/graphs/delete', 'POST', {
                dashboard_name: scopedName,
                graph_id: childId
              });
            }
            if (scopedName) {
              return await apiCall('/api/dashboards/delete', 'POST', {
                dashboard_name: scopedName
              });
            }
          }

          return Promise.resolve();
        } catch (error) {
          console.error('PostgreSQL remove error:', error);
          return Promise.reject(error);
        }
      },

      // Create child reference
      child: function (childPath) {
        return createPostgreSQLRef(`${path}/${childPath}`);
      },

      // Alias for get method
      once: function (eventType) {
        return this.get();
      }
    };
  }

  // Return a PostgreSQL database object
  return {
    ref: function (path) {
      return createPostgreSQLRef(path);
    }
  };
}

// Initialize the database connection once DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  // Initialize PostgreSQL database accessor
  setTimeout(function () {
    try {
      database = getPostgreSQLDatabase();
      dataLog('PostgreSQL database initialized successfully');
      databaseInitAttempted = true;
    } catch (error) {
      console.error('Failed to initialize PostgreSQL database:', error);
      databaseInitAttempted = true;
    }
  }, 100);
});

function dbRef(path) {
  return getDatabaseRef().ref(path);
}

var dataAccessor = (function () {
  var dataAccessorSelf = {};

  dataAccessorSelf.getFilePathByFileName = function (fileName, processedFiles, fileNames) {
    if (typeof processedFiles === 'undefined') {
      processedFiles = userData.processed_file_names;
    }
    if (typeof fileNames === 'undefined') {
      if (!userData.file_names) {
        return null;
      }
      fileNames = userData.file_names.map(x =>
        x.indexOf('-') > -1 ? x.slice(x.indexOf('-') + 1) : x
      );
    }

    // var files = userData.file_names;
    // todo break assumption that its the same length
    var fileIndex = fileNames.indexOf(fileName);
    if (fileIndex > -1) {
      let processedFile = processedFiles[fileIndex];
      if (processedFile && processedFile.indexOf(fileName) > -1) return processedFile;
    }
    // search for any contains
    fileIndex = processedFiles.findIndex(x => x.indexOf(fileName) > -1);
    if (fileIndex > -1) {
      let processedFile = processedFiles[fileIndex];
      return processedFile;
    }
    return null;
  };
  dataAccessorSelf.allFiles = {}; // {file_names: [], processed_files: []}
  dataAccessorSelf.allFolders = [];
  dataAccessorSelf.getAllFiles = function () {
    if (typeof callback !== 'function') {
      callback = function () {};
    }

    // Try to get from localStorage cache first
    const cachedFiles = localStorage.getItem('felix_cached_files');
    const cacheTimestamp = localStorage.getItem('felix_cached_files_timestamp');
    const currentTime = Date.now();

    // Use cache if it's less than 5 minutes old
    if (cachedFiles && cacheTimestamp && currentTime - parseInt(cacheTimestamp) < 5 * 60 * 1000) {
      try {
        const parsedFiles = JSON.parse(cachedFiles);
        dataAccessorSelf.allFiles = parsedFiles;
        dataAccessorSelf.allFolders = parsedFiles.folders || [];
        userData.file_names = parsedFiles.files;
        userData.folder_names = parsedFiles.folders || [];
        userData.processed_file_names =
          parsedFiles.processed_file_names || parsedFiles.processed_files;
        dataLog('Using cached files list');

        // Refresh cache in the background after returning the cached data
        setTimeout(() => {
          fetchFilesFromServer();
        }, 100);

        return Promise.resolve(parsedFiles);
      } catch (e) {
        console.warn('Error parsing cached files:', e);
        // Continue with server fetch on parse error
      }
    }

    // If already loaded in memory, use that
    if (dataAccessorSelf.allFiles && Object.keys(dataAccessorSelf.allFiles).length > 0) {
      return Promise.resolve(dataAccessorSelf.allFiles);
    }

    // Fetch from server
    return fetchFilesFromServer();

    function fetchFilesFromServer() {
      return fetch('/api/get-files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          secret: getUserSecret()
        })
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          // Update memory cache
          dataAccessorSelf.allFiles = data;
          dataAccessorSelf.allFolders = data.folders || [];
          userData.file_names = data.files;
          userData.folder_names = data.folders || [];
          userData.processed_file_names = data.processed_file_names || data.processed_files;

          // Update localStorage cache
          try {
            localStorage.setItem('felix_cached_files', JSON.stringify(data));
            localStorage.setItem('felix_cached_files_timestamp', Date.now().toString());
          } catch (e) {
            console.warn('Error caching files to localStorage:', e);
          }

          return data;
        })
        .catch(error => {
          console.error('Failed to fetch files list', error);
          return [];
        });
    }
  };

  dataAccessorSelf.saveAllNewFiles = function (files) {
    if (!ensureUserData()) {
      console.error('saveAllNewFiles called without valid user data');
      return Promise.reject(new Error('User not authenticated'));
    }
    return fetch(fixtures.inf_server_url + '/api/save-files', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        file_names: files,
        secret: getUserSecret()
      })
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        dataAccessorSelf.allFiles = data;
        dataAccessorSelf.allFolders = data.folders || [];
        userData.file_names = data.files;
        userData.folder_names = data.folders || [];
        userData.processed_file_names = data.processed_file_names || data.processed_files;
        return data;
      })
      .catch(error => {
        console.error('saveAllNewFiles failed', error);
        return [];
      });
  };
  dataAccessorSelf.deleteFile = async function (file) {
    // find the file name in current files
    var currentFiles = dataAccessorSelf.allFiles;
    if (typeof currentFiles === 'object' && currentFiles.file_names) {
      currentFiles = currentFiles.file_names;
    }
    // if empty object or array, try userData
    if (!currentFiles || currentFiles.length <= 0 || typeof currentFiles === 'object') {
      currentFiles = userData.file_names;
    } // sometimes userData.files is where its saved
    // find the longest currentfile ending with -file
    // check file_names key on currentFiles
    var currentFilesMatchings = _.filter(currentFiles, function (currentFile) {
      return currentFile.endsWith('-' + file);
    });
    // sort by length
    currentFilesMatchings = _.sortBy(currentFilesMatchings, function (currentFile) {
      return currentFile.length;
    });
    // delete the longest one
    var fileToDelete = currentFilesMatchings[0];
    return fetch(fixtures.inf_server_url + '/api/delete-files', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        file_names: [fileToDelete],
        secret: getUserSecret() //todo wait for user
      })
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        dataAccessorSelf.allFiles = data;
        dataAccessorSelf.allFolders = data.folders || [];
        userData.file_names = data.files;
        userData.folder_names = data.folders || [];
        userData.processed_file_names = data.processed_file_names || data.processed_files;
        return data;
      })
      .catch(error => {
        console.error('uploadLargeFiles failed', error);
        return [];
      });
  };

  dataAccessorSelf.createFolder = function (folderName) {
    return fetch('/api/create-folder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        folder_name: folderName,
        secret: getUserSecret()
      })
    })
      .then(response => response.json())
      .then(data => {
        dataAccessorSelf.allFolders = data.folders || [];
        userData.folder_names = data.folders || [];
        return data;
      })
      .catch(error => {
        console.error('createFolder failed', error);
        return [];
      });
  };
  dataAccessorSelf.chats = [];
  dataAccessorSelf.isInChat = true;
  dataAccessorSelf.newChat = function (user, chatName) {
    dataAccessorSelf.isInChat = true;

    // Get a database reference (will use mock if needed)
    const db = getDatabaseRef();

    // Create in Firebase
    const promise = db.ref('chats/' + user.secret + '/' + chatName).set({
      name: chatName,
      messages: []
    });

    // Update local cache if it exists
    try {
      const cachedChats = localStorage.getItem('felix_cached_chats');
      if (cachedChats) {
        const parsedChats = JSON.parse(cachedChats);
        if (parsedChats) {
          parsedChats[chatName] = { name: chatName, messages: [] };
          localStorage.setItem('felix_cached_chats', JSON.stringify(parsedChats));
          dataAccessorSelf.chats = parsedChats;
        }
      }
    } catch (e) {
      console.warn('Error updating chat cache after creation:', e);
    }

    // Also ensure current active chat is updated immediately for UI responsiveness
    return promise.then(function(){
      dataAccessorSelf.currentActiveChatName = chatName;
      try {
        localStorage.setItem('felix_cached_chats_timestamp', Date.now().toString());
      } catch (e) {}
      return db.ref('users/' + user.secret).update({ currentActiveChatName: chatName });
    }).catch(function(){
      // Even if backend update fails, keep UI state consistent
      dataAccessorSelf.currentActiveChatName = chatName;
      return Promise.resolve();
    });
  };
  dataAccessorSelf.deleteChat = async function (user, chatName) {
    // if its the current chat then pick another one
    if (dataAccessorSelf.currentActiveChatName === chatName) {
      var chats = dataAccessorSelf.chats;
      if (chats) {
        chats = Object.keys(chats);
        dataAccessorSelf.currentActiveChatName = chats[0];
      }
    }

    // Delete from Firebase
    await getDatabaseRef()
      .ref('chats/' + user.secret + '/' + chatName)
      .remove();

    // Update local cache if it exists
    try {
      const cachedChats = localStorage.getItem('felix_cached_chats');
      if (cachedChats) {
        const parsedChats = JSON.parse(cachedChats);
        if (parsedChats && parsedChats[chatName]) {
          delete parsedChats[chatName];
          localStorage.setItem('felix_cached_chats', JSON.stringify(parsedChats));
          dataAccessorSelf.chats = parsedChats;
        }
      }
    } catch (e) {
      console.warn('Error updating chat cache after deletion:', e);
    }

    return Promise.resolve();
  };
  dataAccessorSelf.getChats = function () {
    // Try to load from cache first
    const cachedChats = localStorage.getItem('felix_cached_chats');
    const cacheTimestamp = localStorage.getItem('felix_cached_chats_timestamp');
    const currentTime = Date.now();

    // Use cache if it's less than 2 minutes old
    if (cachedChats && cacheTimestamp && currentTime - parseInt(cacheTimestamp) < 2 * 60 * 1000) {
      try {
        const parsedChats = JSON.parse(cachedChats);
        dataAccessorSelf.chats = parsedChats;
        dataLog('Using cached chats list');

        // Refresh cache in the background
        setTimeout(() => {
          fetchChatsFromFirebase();
        }, 100);

        return Promise.resolve(parsedChats);
      } catch (e) {
        console.warn('Error parsing cached chats:', e);
        // Continue with Firebase fetch on parse error
      }
    }

    // If already loaded in memory, use that
    if (dataAccessorSelf.chats && Object.keys(dataAccessorSelf.chats || {}).length > 0) {
      return Promise.resolve(dataAccessorSelf.chats);
    }

    // Fetch from Firebase
    return fetchChatsFromFirebase();

    function fetchChatsFromFirebase() {
      return getDatabaseRef()
        .ref('chats/' + userData.secret)
        .get()
        .then(function (snapshot) {
          const chats = snapshot.val() || {};
          dataAccessorSelf.chats = chats;

          // Update localStorage cache
          try {
            localStorage.setItem('felix_cached_chats', JSON.stringify(chats));
            localStorage.setItem('felix_cached_chats_timestamp', Date.now().toString());
          } catch (e) {
            console.warn('Error caching chats to localStorage:', e);
          }

          return chats;
        });
    }
  };
  dataAccessorSelf.getChat = function (user, chatName) {
    return getDatabaseRef()
      .ref('chats/' + user.secret + '/' + chatName)
      .get()
      .then(function (snapshot) {
        return snapshot.val();
      });
  };
  dataAccessorSelf.getChatMessage = function (user, chatName, messageId) {
    return getDatabaseRef()
      .ref('chats/' + user.secret + '/' + chatName + '/messages/' + messageId)
      .get()
      .then(function (snapshot) {
        let val = snapshot.val();
        if (val && val.edits && typeof val.edits === 'string') {
          val.edits = [val.edits];
        }
        return val;
      });
  };
  dataAccessorSelf.addMessageToChat = function (user, chatName, messageId, messageName) {
    return getDatabaseRef()
      .ref('chats/' + user.secret + '/' + chatName + '/messages/' + messageId)
      .set({
        messageName: messageName,
        edits: []
      });
  };
  dataAccessorSelf.deleteMessageFromChat = function (user, chatName, messageId) {
    return getDatabaseRef()
      .ref('chats/' + user.secret + '/' + chatName + '/messages/' + messageId)
      .remove();
  };

  dataAccessorSelf.addEditsToChat = function (user, chatName, messageId, messageName, edits) {
    if (typeof edits === 'string') {
      edits = [edits];
    }
    var update = {
      edits: edits,
      messageName: messageName
    };
    if (typeof edits == 'undefined') {
      update = {
        messageName: messageName
      };
    }
    return getDatabaseRef()
      .ref('chats/' + user.secret + '/' + chatName)
      .child('messages')
      .child(messageId)
      .update(update);
  };
  dataAccessorSelf.addMessageToCurrentActiveChat = function (user, messageId, messageName) {
    return dataAccessorSelf.addMessageToChat(
      user,
      dataAccessorSelf.currentActiveChatName,
      messageId,
      messageName
    );
  };

  dataAccessorSelf.renameChat = async function (user, oldChatName, newChatName) {
    if (!user || !user.secret || !oldChatName || !newChatName || oldChatName === newChatName) {
      return Promise.resolve();
    }

    const db = getDatabaseRef();
    const oldRef = db.ref('chats/' + user.secret + '/' + oldChatName);
    const newRef = db.ref('chats/' + user.secret + '/' + newChatName);
    const oldSnapshot = await oldRef.get();
    const oldChat = oldSnapshot.val();
    if (!oldChat) {
      return Promise.resolve();
    }

    const newSnapshot = await newRef.get();
    if (newSnapshot.val()) {
      throw new Error('A chat named "' + newChatName + '" already exists');
    }

    const renamedChat = Object.assign({}, oldChat, { name: newChatName });
    await apiCall('/api/chats/rename', 'POST', {
      old_chat_name: oldChatName,
      new_chat_name: newChatName
    });

    if (dataAccessorSelf.currentActiveChatName === oldChatName) {
      dataAccessorSelf.currentActiveChatName = newChatName;
      await db.ref('users/' + user.secret).update({ currentActiveChatName: newChatName });
    }

    try {
      const cachedChats = localStorage.getItem('felix_cached_chats');
      if (cachedChats) {
        const parsedChats = JSON.parse(cachedChats);
        if (parsedChats && parsedChats[oldChatName]) {
          parsedChats[newChatName] = renamedChat;
          delete parsedChats[oldChatName];
          localStorage.setItem('felix_cached_chats', JSON.stringify(parsedChats));
          localStorage.setItem('felix_cached_chats_timestamp', Date.now().toString());
          dataAccessorSelf.chats = parsedChats;
        }
      }
    } catch (e) {
      console.warn('Error updating chat cache after rename:', e);
    }
  };

  dataAccessorSelf.currentActiveChatName = 'general';
  dataAccessorSelf.setCurrentActiveChat = function (user, chatName) {
    if (!user || !user.secret) {
      console.warn('setCurrentActiveChat called without valid user');
      return Promise.resolve();
    }

    dataAccessorSelf.currentActiveChatName = chatName;
    dataAccessorSelf.isInChat = true;
    try {
      localStorage.setItem('felix_active_surface', 'chat');
    } catch (e) {}
    return getDatabaseRef()
      .ref('users/' + user.secret)
      .update({
        currentActiveChatName: chatName
      })
      .catch(function (error) {
        console.warn('Non-fatal: failed to persist currentActiveChatName, continuing', error);
        return Promise.resolve();
      });
  };

  dataAccessorSelf.getCurrentActiveChatName = function (user) {
    if (dataAccessorSelf.currentActiveChatName && dataAccessorSelf.currentActiveChatName !== 'Untitled') {
      return Promise.resolve(dataAccessorSelf.currentActiveChatName);
    }
    if (!user || !user.secret) {
      console.warn('getCurrentActiveChatName called without valid user');
      return Promise.resolve(dataAccessorSelf.currentActiveChatName || null);
    }

    // Double-check user.secret is not null before database access
    if (user.secret === null || user.secret === undefined) {
      console.warn('getCurrentActiveChatName: user.secret is null or undefined');
      return Promise.resolve(dataAccessorSelf.currentActiveChatName || null);
    }

    return getDatabaseRef()
      .ref('users/' + user.secret)
      .once('value')
      .then(function (snapshot) {
        let val = snapshot.val() ? snapshot.val().currentActiveChatName : null;
        if (!val) {
          val = dataAccessorSelf.currentActiveChatName;
        }
        dataAccessorSelf.currentActiveChatName = val;
        return val;
      })
      .catch(function (error) {
        console.warn('Error getting current active chat name:', error);
        return dataAccessorSelf.currentActiveChatName;
      });
  };
  dataAccessorSelf.getCurrentChat = function (user) {
    return dataAccessorSelf.getCurrentActiveChatName(user).then(async function (chatName) {
      if (!user || !user.secret) {
        return null;
      }
      let resolvedChatName = chatName || dataAccessorSelf.currentActiveChatName || 'general';
      let chat = null;
      if (resolvedChatName) {
        try {
          chat = await dataAccessorSelf.getChat(user, resolvedChatName);
        } catch (error) {
          console.warn('Unable to load current chat:', error);
        }
      }
      if (chat) {
        return chat;
      }

      let chats = {};
      try {
        chats = (await dataAccessorSelf.getChats()) || {};
      } catch (error) {
        console.warn('Unable to load chat list for fallback:', error);
      }
      const chatNames = Object.keys(chats || {});
      if (chatNames.length > 0) {
        const fallbackName = chats[resolvedChatName] ? resolvedChatName : chatNames[0];
        dataAccessorSelf.currentActiveChatName = fallbackName;
        return chats[fallbackName] || { name: fallbackName, messages: [] };
      }

      if (resolvedChatName) {
        try {
          await dataAccessorSelf.newChat(user, resolvedChatName);
        } catch (error) {
          console.warn('Unable to create fallback chat:', error);
        }
      }
      return { name: resolvedChatName, messages: [] };
    });
  };
  dataAccessorSelf.getCurrentActiveChat = dataAccessorSelf.getCurrentChat;

  dataAccessorSelf.updateChatData = function (user, chatName, chatId, chatData) {
    return getDatabaseRef()
      .ref('chats/' + user.secret + '/' + chatName)
      .child('messages')
      .child(chatId)
      .set(chatData);
  };

  dataAccessorSelf.getChatData = function (user, chatName, chatId) {
    return getDatabaseRef()
      .ref('chats/' + user.secret + '/' + chatName)
      .get()
      .then(function (snapshot) {
        const data = snapshot.val();
        if (!data || !data.messages) {
          // Return empty data structure if no messages
          return { messageName: '', edits: [] };
        }

        let messages = data.messages;
        var finalGraph = messages[chatId];

        if (!finalGraph) {
          // Return empty data structure if specific chat message not found
          return { messageName: '', edits: [] };
        }

        if (!finalGraph.edits) {
          //empty is not stored in firebase
          finalGraph.edits = [];
        }
        return finalGraph;
      })
      .catch(function (error) {
        console.warn('Error getting chat data:', error);
        // Return a fallback empty structure
        return { messageName: '', edits: [] };
      });
  };

  dataAccessorSelf.dashboards = [];
  // dataAccessorSelf.getDashboards = function () {
  dataAccessorSelf.dashboardIdToGraphs = {}; // graphs = {name: graphName, edits: graphEdits}
  // keep track of all dashboards in the firebase database
  // var dashboardsRef = dbRef('dashboards');

  dataAccessorSelf.newDashboard = function (user, dashboardName) {
    dataAccessorSelf.isInChat = false;

    // Create in Firebase
    const promise = dbRef('dashboards/' + user.secret + '/' + dashboardName).set({
      name: dashboardName,
      graphs: {} //el to graphmetadata map?
    });

    // Update local cache if it exists
    try {
      const cachedDashboards = localStorage.getItem('felix_cached_dashboards');
      if (cachedDashboards) {
        const parsedDashboards = JSON.parse(cachedDashboards);
        if (parsedDashboards) {
          parsedDashboards[dashboardName] = { name: dashboardName, graphs: {} };
          localStorage.setItem('felix_cached_dashboards', JSON.stringify(parsedDashboards));
          dataAccessorSelf.dashboards = parsedDashboards;
        }
      }
    } catch (e) {
      console.warn('Error updating dashboard cache after creation:', e);
    }

    return promise;
  };

  dataAccessorSelf.deleteDashboard = async function (user, dashboardName) {
    // if its the current dashboard then pick another one
    if (dataAccessorSelf.currentActiveDashboardName === dashboardName) {
      var dashboards = dataAccessorSelf.dashboards;
      if (dashboards) {
        dashboards = Object.keys(dashboards);
        dataAccessorSelf.currentActiveDashboardName = dashboards[0];
        await dataAccessorSelf.setCurrentActiveDashboard(user, dashboards[0]);
      }
    }

    // Delete from Firebase
    await dbRef('dashboards/' + user.secret + '/' + dashboardName).remove();

    // Update local cache if it exists
    try {
      const cachedDashboards = localStorage.getItem('felix_cached_dashboards');
      if (cachedDashboards) {
        const parsedDashboards = JSON.parse(cachedDashboards);
        if (parsedDashboards && parsedDashboards[dashboardName]) {
          delete parsedDashboards[dashboardName];
          localStorage.setItem('felix_cached_dashboards', JSON.stringify(parsedDashboards));
          dataAccessorSelf.dashboards = parsedDashboards;
        }
      }
    } catch (e) {
      console.warn('Error updating dashboard cache after deletion:', e);
    }

    return Promise.resolve();
  };

  dataAccessorSelf.renameDashboard = async function (user, oldName, newName) {
    if (!user || !user.secret || !oldName || !newName || oldName === newName) {
      return Promise.resolve();
    }

    const oldRef = dbRef('dashboards/' + user.secret + '/' + oldName);
    const newRef = dbRef('dashboards/' + user.secret + '/' + newName);
    const oldSnapshot = await oldRef.get();
    const oldDashboard = oldSnapshot.val();
    if (!oldDashboard) {
      return Promise.resolve();
    }

    const newSnapshot = await newRef.get();
    if (newSnapshot.val()) {
      throw new Error('A dashboard named "' + newName + '" already exists');
    }

    const renamedDashboard = Object.assign({}, oldDashboard, { name: newName });
    if (typeof apiCall === 'function') {
      await apiCall('/api/dashboards/rename', 'POST', {
        old_dashboard_name: oldName,
        new_dashboard_name: newName
      });
    } else {
      await newRef.set(renamedDashboard);
      await oldRef.remove();
    }

    if (dataAccessorSelf.currentActiveDashboardName === oldName) {
      dataAccessorSelf.currentActiveDashboardName = newName;
    }

    try {
      const cachedDashboards = localStorage.getItem('felix_cached_dashboards');
      if (cachedDashboards) {
        const parsedDashboards = JSON.parse(cachedDashboards);
        if (parsedDashboards && parsedDashboards[oldName]) {
          parsedDashboards[newName] = renamedDashboard;
          delete parsedDashboards[oldName];
          localStorage.setItem('felix_cached_dashboards', JSON.stringify(parsedDashboards));
          dataAccessorSelf.dashboards = parsedDashboards;
        }
      }
    } catch (e) {
      console.warn('Error updating dashboard cache after rename:', e);
    }
  };

  dataAccessorSelf.getDashboards = function (user) {
    // Check if user data is available
    if (!user || !user.secret) {
      console.warn('getDashboards called without valid user data');
      return Promise.resolve({});
    }

    // Try to load from cache first
    const cachedDashboards = localStorage.getItem('felix_cached_dashboards');
    const cacheTimestamp = localStorage.getItem('felix_cached_dashboards_timestamp');
    const currentTime = Date.now();

    // Use cache if it's less than 2 minutes old
    if (
      cachedDashboards &&
      cacheTimestamp &&
      currentTime - parseInt(cacheTimestamp) < 2 * 60 * 1000
    ) {
      try {
        const parsedDashboards = JSON.parse(cachedDashboards);
        dataAccessorSelf.dashboards = parsedDashboards;
        dataLog('Using cached dashboards list');

        // Refresh cache in the background
        setTimeout(() => {
          // Make sure user is still valid when timeout executes
          const currentUser = user || window.userData;
          if (currentUser && currentUser.secret) {
            fetchDashboardsFromFirebase(currentUser);
          } else {
            console.warn('User data not available for background dashboard refresh');
          }
        }, 100);

        return Promise.resolve(parsedDashboards);
      } catch (e) {
        console.warn('Error parsing cached dashboards:', e);
        // Continue with Firebase fetch on parse error
      }
    }

    // If already loaded in memory, use that
    if (dataAccessorSelf.dashboards && Object.keys(dataAccessorSelf.dashboards || {}).length > 0) {
      return Promise.resolve(dataAccessorSelf.dashboards);
    }

    // Fetch from Firebase
    return fetchDashboardsFromFirebase(user);

    function fetchDashboardsFromFirebase(user) {
      if (!user || !user.secret) {
        console.warn('fetchDashboardsFromFirebase called without valid user data');
        return Promise.resolve({});
      }

      // Double-check user.secret is not null before database access
      if (user.secret === null || user.secret === undefined) {
        console.warn('fetchDashboardsFromFirebase: user.secret is null or undefined');
        return Promise.resolve({});
      }

      return dbRef('dashboards/' + user.secret)
        .get()
        .then(function (snapshot) {
          const dashboards = snapshot.val() || {};
          dataAccessorSelf.dashboards = dashboards;

          // Update localStorage cache
          try {
            localStorage.setItem('felix_cached_dashboards', JSON.stringify(dashboards));
            localStorage.setItem('felix_cached_dashboards_timestamp', Date.now().toString());
          } catch (e) {
            console.warn('Error caching dashboards to localStorage:', e);
          }

          return dashboards;
        });
    }
  };
  // Ensure dashboards always have a stable shape
  function normalizeDashboard(dashboardName, dashboardData) {
    const safeGraphs =
      dashboardData && typeof dashboardData === 'object' && dashboardData.graphs
        ? dashboardData.graphs
        : {};
    return {
      name:
        (dashboardData && dashboardData.name) ||
        dashboardName ||
        dataAccessorSelf.currentActiveDashboardName ||
        'Untitled',
      graphs: safeGraphs,
      gridConfig: (dashboardData && dashboardData.gridConfig) || null
    };
  }

  dataAccessorSelf.getDashboard = async function (user, dashboardName) {
    const fallback = normalizeDashboard(dashboardName, null);

    if (!user || !user.secret) {
      console.warn('getDashboard called without valid user; returning empty dashboard');
      return Promise.resolve(fallback);
    }

    // Use in-memory cache first
    if (dataAccessorSelf.dashboards && dataAccessorSelf.dashboards[dashboardName]) {
      return Promise.resolve(normalizeDashboard(dashboardName, dataAccessorSelf.dashboards[dashboardName]));
    }

    // Otherwise try to fetch and gracefully fall back on any failure
    try {
      const snapshot = await dbRef('dashboards/' + user.secret + '/' + dashboardName).get();
      const raw =
        snapshot && typeof snapshot.val === 'function'
          ? snapshot.val()
          : snapshot && snapshot.val
            ? snapshot.val
            : snapshot;

      if (raw) {
        const normalized = normalizeDashboard(dashboardName, raw);
        // hydrate caches so subsequent reads are instant
        dataAccessorSelf.dashboards = Object.assign({}, dataAccessorSelf.dashboards, {
          [normalized.name]: normalized
        });
        try {
          localStorage.setItem('felix_cached_dashboards', JSON.stringify(dataAccessorSelf.dashboards));
          localStorage.setItem('felix_cached_dashboards_timestamp', Date.now().toString());
        } catch (e) {
          console.warn('Error caching dashboard after fetch:', e);
        }
        return normalized;
      }
    } catch (error) {
      console.warn('Error getting dashboard, using fallback:', error);
    }

    // As a last resort, create a placeholder so the UI can keep working
    if (dashboardName) {
      try {
        await dataAccessorSelf.newDashboard(user, dashboardName);
      } catch (e) {
        // best-effort creation; keep going even if this fails
      }
    }
    return fallback;
  };
  dataAccessorSelf.addGraphToDashboard = function (user, dashboardName, graphId, graphName) {
    // graphname is the query that
    return dbRef('dashboards/' + user.secret + '/' + dashboardName)
      .child('graphs')
      .child(graphId)
      .set({
        graphName: graphName,
        edits: []
      });
  };

  dataAccessorSelf.addGraphToCurrentDashboard = function (user, graphId, graphName) {
    // graphname is the query that
    var dashboardName = dataAccessorSelf.currentActiveDashboardName;
    return dbRef('dashboards/' + user.secret + '/' + dashboardName)
      .child('graphs')
      .child(graphId)
      .set({
        graphName: graphName,
        edits: []
      });
  };
  dataAccessorSelf.saveGridConfigForCurrentDashboard = function (user, gridConfig) {
    var dashboardName = dataAccessorSelf.currentActiveDashboardName;
    return dbRef('dashboards/' + user.secret + '/' + dashboardName).update({
      gridConfig: gridConfig
    });
  };

  dataAccessorSelf.addEditsToGraph = function (user, dashboardName, graphId, graphName, edits) {
    if (typeof edits === 'string') {
      edits = [edits];
    }
    return dbRef('dashboards/' + user.secret + '/' + dashboardName)
      .child('graphs')
      .child(graphId)
      .update({
        edits: edits,
        graphName: graphName
      });
  };
  dataAccessorSelf.updateGraphData = function (user, dashboardName, graphId, graphData) {
    return dbRef('dashboards/' + user.secret + '/' + dashboardName)
      .child('graphs')
      .child(graphId)
      .update(graphData);
  };
  // get the data once
  dataAccessorSelf.getGraphData = function (user, dashboardName, graphId) {
    //uniuqe id?
    return dbRef('dashboards/' + user.secret + '/' + dashboardName)
      .get()
      .then(function (snapshot) {
        let graphs = snapshot.val().graphs;
        dataAccessorSelf.dashboardIdToGraphs[graphId] = graphs;
        var finalGraph = graphs[graphId];
        if (!finalGraph.edits) {
          //empty is not stored in firebase
          finalGraph.edits = [];
        }
        if (typeof finalGraph.edits === 'string') {
          finalGraph.edits = [finalGraph.edits];
        }
        return finalGraph;
      });
  };
  dataAccessorSelf.getAllGraphs = function () {
    //unique id?
    return dbRef('dashboards')
      .get()
      .then(function (snapshot) {
        var allGraphNames = [];
        let dashboards = snapshot && typeof snapshot.val === 'function' ? snapshot.val() : null;
        if (!dashboards || typeof dashboards !== 'object') {
          return allGraphNames;
        }
        // get all subkeys and any dashboard name in secret/dashboardname
        let secrets = Object.keys(dashboards);
        for (var i = 0; i < secrets.length; i++) {
          var perSecret = dashboards[secrets[i]];
          if (!perSecret || typeof perSecret !== 'object') {
            continue;
          }
          var dashboardNames = Object.keys(perSecret);
          for (var j = 0; j < dashboardNames.length; j++) {
            var dashboardEntry = perSecret[dashboardNames[j]];
            let currentGraphs = dashboardEntry && dashboardEntry.graphs;
            if (!currentGraphs || typeof currentGraphs !== 'object') {
              continue;
            }
            try {
              var graphIds = Object.keys(currentGraphs);
              for (var k = 0; k < graphIds.length; k++) {
                allGraphNames.push(currentGraphs[graphIds[k]].graphName);
              }
            } catch (e) {
              console.error('Error reading graph ids', e);
            }
          }
        }

        return allGraphNames;
      })
      .catch(function (err) {
        console.error('getAllGraphs failed', err);
        return [];
      });
  };

  dataAccessorSelf.deleteGraphFromDashboard = function (user, dashboardName, graphId) {
    return dbRef('dashboards/' + user.secret + '/' + dashboardName)
      .child('graphs')
      .child(graphId)
      .remove();
  };
  dataAccessorSelf.deleteGraphFromCurrentDashboard = function (user, graphId) {
    var dashboardName = dataAccessorSelf.currentActiveDashboardName;
    return dbRef('dashboards/' + user.secret + '/' + dashboardName)
      .child('graphs')
      .child(graphId)
      .remove();
  };
  dataAccessorSelf.refreshGraphFromCurrentDashboard = function (user, graphId) {
    var dashboardName = dataAccessorSelf.currentActiveDashboardName;
    return dbRef('dashboards/' + user.secret + '/' + dashboardName)
      .child('graphs')
      .child(graphId)
      .get()
      .then(function (snapshot) {
        let val = snapshot.val();
        if (!val) {
          return;
        }
        let graphName = val.graphName;
        if (graphName.endsWith(' -')) {
          graphName = graphName + '-';
        } else {
          graphName = graphName + ' -';
        }
        return dbRef('dashboards/' + user.secret + '/' + dashboardName)
          .child('graphs')
          .child(graphId)
          .update({
            graphName: graphName
          });
      });
  };

  //get and set the current active dashboard for a user
  dataAccessorSelf.setCurrentActiveDashboard = function (user, dashboardName) {
    dataAccessorSelf.currentActiveDashboardName = dashboardName;
    dataAccessorSelf.isInChat = false;
    try {
      localStorage.setItem('felix_active_surface', 'dashboard');
    } catch (e) {}
    if (!user || !user.secret) {
      console.warn('setCurrentActiveDashboard called without valid user');
      return Promise.resolve();
    }
    return dbRef('users/' + user.secret).update({
      currentActiveDashboard: dashboardName
    });
  };

  dataAccessorSelf.currentActiveDashboardName = 'Untitled';

  dataAccessorSelf.getCurrentActiveDashboardName = function (user) {
    if (dataAccessorSelf.currentActiveDashboardName && dataAccessorSelf.currentActiveDashboardName !== 'Untitled') {
      return Promise.resolve(dataAccessorSelf.currentActiveDashboardName);
    }
    if (!user || !user.secret) {
      console.warn('getCurrentActiveDashboardName called without valid user');
      return Promise.resolve(dataAccessorSelf.currentActiveDashboardName);
    }

    return dbRef('users/' + user.secret)
      .once('value')
      .then(function (snapshot) {
      let val = snapshot.val();
      if (!val) {
        return dataAccessorSelf.currentActiveDashboardName; // untitled
      }
      if (!val.currentActiveDashboard) {
        return dataAccessorSelf.currentActiveDashboardName;
      }
      dataAccessorSelf.currentActiveDashboardName = val.currentActiveDashboard;
      return dataAccessorSelf.currentActiveDashboardName;
      })
      .catch(function (error) {
        console.warn('Error getting current active dashboard name:', error);
        return dataAccessorSelf.currentActiveDashboardName;
      });
  };

  dataAccessorSelf.getCurrentDashboard = function (user) {
    return dataAccessorSelf.getCurrentActiveDashboardName(user).then(function (dashboardName) {
      return dataAccessorSelf.getDashboard(user, dashboardName);
    });
  };

  // dashboardsRef.on('value', function(snapshot) {
  //     dataAccessorSelf.dashboards = snapshot.val();
  // })
  //todo listen to only the current visible dashboard for collaboration
  // dbRef('dashboards/' + user.secret + '/' + dashboardName).on('value', function (snapshot) {
  //
  //     })

  // Function to clear all caches (useful when logging out)
  dataAccessorSelf.clearAllCaches = function () {
    // Clear localStorage caches
    try {
      localStorage.removeItem('felix_cached_files');
      localStorage.removeItem('felix_cached_files_timestamp');
      localStorage.removeItem('felix_cached_chats');
      localStorage.removeItem('felix_cached_chats_timestamp');
      localStorage.removeItem('felix_cached_dashboards');
      localStorage.removeItem('felix_cached_dashboards_timestamp');
      localStorage.removeItem('cached_user_data');
      localStorage.removeItem('cached_user_timestamp');

      // Clear memory caches
      dataAccessorSelf.allFiles = {};
      dataAccessorSelf.chats = [];
      dataAccessorSelf.dashboards = [];

      // Clear IndexedDB graph cache if it exists
      if (typeof graphCache !== 'undefined' && graphCache.clearCache) {
        graphCache.clearCache();
      }

      dataLog('All caches cleared');
    } catch (e) {
      console.warn('Error clearing caches:', e);
    }
  };

  return dataAccessorSelf;
})();

// Make dataAccessor available globally
window.dataAccessor = dataAccessor;
